import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';

interface AddMealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (name: string, time: string, notes?: string, calories?: number) => void;
  meal?: { id: string; name: string; time: Date; notes?: string; calories?: number } | null;
}

export function AddMealDialog({ open, onOpenChange, onSuccess, meal }: AddMealDialogProps) {
  const { t, dir } = useLanguage();
  const [name, setName] = useState('');
  const [time, setTime] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [notes, setNotes] = useState('');
  const [calories, setCalories] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (meal) {
      setName(meal.name);
      setTime(format(meal.time, "yyyy-MM-dd'T'HH:mm"));
      setNotes(meal.notes || '');
      setCalories(meal.calories?.toString() || '');
    } else {
      setName('');
      setTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      setNotes('');
      setCalories('');
    }
  }, [meal, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      return;
    }

    setLoading(true);
    try {
      const caloriesNum = calories ? parseFloat(calories) : undefined;
      onSuccess(name, time, notes, caloriesNum);
      handleClose();
    } catch (error) {
      console.error('Error submitting meal:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!meal) {
      setName('');
      setTime(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      setNotes('');
      setCalories('');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} dir={dir}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {meal ? t('meals.editMeal') || 'Edit Meal' : t('meals.addMeal') || 'Log Meal'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('meals.mealName') || 'Meal Name'}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('meals.mealNamePlaceholder') || 'e.g., Grilled Chicken Salad'}
              required
              dir={dir}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="time">{t('meals.mealTime') || 'Time'}</Label>
            <Input
              id="time"
              type="datetime-local"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="calories">{t('meals.calories') || 'Calories (Optional)'}</Label>
            <Input
              id="calories"
              type="number"
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              placeholder={t('meals.caloriesPlaceholder') || 'e.g., 500'}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('meals.notes') || 'Notes (Optional)'}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('meals.notesPlaceholder') || 'Add any notes about this meal...'}
              rows={3}
              dir={dir}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading || !name.trim()}>
              {loading
                ? (t('common.saving') || 'Saving...')
                : (meal ? t('common.save') || 'Save' : t('common.add') || 'Add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

